import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { useUserActivity } from '@/hooks/useUserActivity';
import eventemitter from '@/lib/eventEmitter';
import { Result } from '@/lib/type';
import { useOrgContext } from '@/contexts/orgContext';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { calculateRoleForSpace } from '@/pages/admin/user/type';
import { getWorkSpaceDetail } from '@/pages/workgroup/api';
import {
  PAUSED_WORKSPACE_DETECTED_EVENT,
  PausedWorkspaceDetectedPayload,
} from '@/pages/workgroup/autoPause/pausedWorkspaceDetection';
import {
  AUTO_PAUSE_POLL_INTERVAL_MS,
  AUTO_PAUSE_WARN_LEAD_MS,
  AUTO_STOP_DISABLED_MINUTES,
} from '@/pages/workgroup/form/setting/constants';
import { useMutationKeepAliveWorkspace } from '@/pages/workgroup/hook';
import { isStatusActive, isStatusIdle, isStatusPaused, WorkspaceT } from '@/pages/workgroup/type';

const TICK_INTERVAL_MS = 1000;
// Floor between automatic pings. The deadline only moves once the request lands and the cache is
// patched, so without this the guard would re-ping on every tick while a ping is in flight - and
// would ping every second forever if the deadline failed to advance at all.
const KEEP_ALIVE_MIN_INTERVAL_MS = 60 * 1000;

export type AutoPauseGuardState = {
  // The workspace the warning modal is about, or null when no warning is showing.
  warningWorkspace: WorkspaceT | null;
  // Milliseconds until the projected auto-pause, clamped at 0.
  msRemaining: number;
  // The workspace to offer a resume for, or null when none is paused.
  pausedWorkspace: WorkspaceT | null;
  isExtending: boolean;
  onKeepAlive: () => void;
  onDismissWarning: () => void;
  // Cancel on the resume prompt: the user chose not to resume, so move off the paused workspace.
  onDismissResume: () => void;
  // The resume request was accepted: keep the workspace selected while it comes back.
  onResumed: () => void;
};

/**
 * The detail endpoint returns the bare workspace. The fields calculatePropsForWorkspace copies from
 * its workgroup (platform and region price it in the resume dialog) only exist on the copy that came
 * through the workgroup list, so carry them over from there.
 */
function withWorkgroupProps(workspace: WorkspaceT, listed: WorkspaceT): WorkspaceT {
  return {
    ...workspace,
    workgroup_name: listed.workgroup_name,
    cloud_provider_id: listed.cloud_provider_id,
    platform: listed.platform,
    region: listed.region,
    database_name: listed.database_name,
    canAccess: listed.canAccess,
  };
}

/**
 * Drives the auto-pause warning, the activity-gated keep-alive, and the paused-workspace resume
 * prompt for the currently selected workspace.
 */
export function useAutoPauseGuard(enabled: boolean): AutoPauseGuardState {
  const { currentWorkspace, leaveCurrentWorkspace } = useWorkspaceContext();
  const { userInfo } = useOrgContext();
  const { isActive } = useUserActivity();
  const keepAliveMutation = useMutationKeepAliveWorkspace();

  const [now, setNow] = useState(() => Date.now());
  const [isWarningVisible, setWarningVisible] = useState(false);
  const [pausedWorkspace, setPausedWorkspace] = useState<WorkspaceT | null>(null);
  // The deadline the workspace carried when it was seen paused. The backend keeps the heartbeat row
  // across a pause, so a paused workspace still reports its old (past) deadline, and a resumed one
  // keeps reporting it until the agent's first heartbeat lands. Trusting it would show a 00:00
  // warning for a workspace that is not about to pause.
  const [staleDeadline, setStaleDeadline] = useState<string | null>(null);

  // Opting in is remembered per workspace and only for this page session: switching workspaces or
  // reloading should put the user back in control rather than silently keeping something alive.
  const optedInWorkspaceIdRef = useRef<string | null>(null);
  // The deadline the user declined, so we do not immediately re-prompt for the same one.
  const dismissedDeadlineRef = useRef<string | null>(null);
  // auto_stop_after is a server timestamp; without this a skewed client clock would show a wrong
  // or negative countdown.
  const skewMsRef = useRef(0);
  // The resume dialog wants the workspace as it was while Active. workspaceContext keeps a paused
  // selection only on tool pages and only for its admins, so keep our own handle as well.
  const lastTrackedWorkspaceRef = useRef<WorkspaceT | null>(null);
  // A workspace the user just resumed from the prompt. Until it is back to Active, both the poll and
  // API responses still see it paused, and must not prompt again.
  const resumingWorkspaceIdRef = useRef<string | null>(null);
  const lastKeepAliveAtRef = useRef(0);
  // Read by the API detection listener, which is registered once and would otherwise see a stale
  // deadline.
  const latestDeadlineRef = useRef<string | undefined>(undefined);

  const hasPermission = useMemo(() => {
    if (!currentWorkspace || !userInfo?.roles) {
      return false;
    }
    return (
      calculateRoleForSpace(userInfo.roles, currentWorkspace.workgroup_id, currentWorkspace.workspace_id) ===
      'workspace-admins'
    );
  }, [currentWorkspace, userInfo]);

  const autoStopEnabled =
    !!currentWorkspace &&
    (currentWorkspace.auto_stop_minutes ?? AUTO_STOP_DISABLED_MINUTES) > AUTO_STOP_DISABLED_MINUTES;

  useEffect(() => {
    if (currentWorkspace && isStatusActive(currentWorkspace.status)) {
      lastTrackedWorkspaceRef.current = currentWorkspace;
    }
  }, [currentWorkspace]);

  // Reset per-workspace state when the selection changes.
  const workspaceId = currentWorkspace?.workspace_id;
  useEffect(() => {
    optedInWorkspaceIdRef.current = null;
    dismissedDeadlineRef.current = null;
    setWarningVisible(false);
    setStaleDeadline(null);
  }, [workspaceId]);

  // A pause ends the session the user opted in for: after a resume they decide again.
  const forgetDecisions = useCallback(() => {
    optedInWorkspaceIdRef.current = null;
    dismissedDeadlineRef.current = null;
    lastKeepAliveAtRef.current = 0;
  }, []);

  // Tool pages do no workspace status polling of their own. This both refreshes the countdown (so
  // activity from anyone else extends it too) and catches the Active -> Idle/Stopped transition. It
  // runs with auto suspend off too: a workspace paused by hand from elsewhere needs the prompt as well.
  const { data: polledWorkspace } = useQuery<Result<WorkspaceT>, unknown>(
    ['space', workspaceId],
    () => getWorkSpaceDetail(currentWorkspace!.workgroup_id, currentWorkspace!.workspace_id),
    {
      enabled: enabled && !!currentWorkspace && hasPermission,
      refetchInterval: AUTO_PAUSE_POLL_INTERVAL_MS,
      refetchIntervalInBackground: false,
    }
  );

  // Detect a pause from polling.
  useEffect(() => {
    const workspace = polledWorkspace?.Result;
    if (!workspace) {
      return;
    }
    if (workspace.workspace_id === resumingWorkspaceIdRef.current) {
      if (isStatusActive(workspace.status)) {
        resumingWorkspaceIdRef.current = null;
      } else {
        return;
      }
    }
    if (!isStatusActive(workspace.status)) {
      setWarningVisible(false);
      setStaleDeadline(workspace.auto_stop_after ?? null);
      forgetDecisions();
    }
    const tracked = lastTrackedWorkspaceRef.current;
    if (
      (isStatusIdle(workspace.status) || isStatusPaused(workspace.status)) &&
      tracked?.workspace_id === workspace.workspace_id
    ) {
      setPausedWorkspace((current) => current ?? withWorkgroupProps(workspace, tracked));
    }
  }, [polledWorkspace, forgetDecisions]);

  // Detect a pause from an API response.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onDetected = (payload: PausedWorkspaceDetectedPayload) => {
      const tracked = lastTrackedWorkspaceRef.current;
      if (!tracked) {
        return;
      }
      // A response may not name the workspace; when it does, ignore anything that is not the one
      // the user is looking at.
      if (payload.workspaceId && payload.workspaceId !== tracked.workspace_id) {
        return;
      }
      if (tracked.workspace_id === resumingWorkspaceIdRef.current) {
        return;
      }
      setWarningVisible(false);
      setStaleDeadline(latestDeadlineRef.current ?? null);
      forgetDecisions();
      setPausedWorkspace((current) => current ?? tracked);
    };

    eventemitter.on(PAUSED_WORKSPACE_DETECTED_EVENT, onDetected);
    return () => {
      eventemitter.off(PAUSED_WORKSPACE_DETECTED_EVENT, onDetected);
    };
  }, [enabled, forgetDecisions]);

  // Prefer the polled detail: workspaceContext only replaces currentWorkspace when its id, name or
  // access changes, so its auto_stop_after is frozen at selection time. The poll picks up extensions
  // from agent heartbeats, and the keep-alive mutation patches the same ['space', id] cache entry.
  const polledCurrentWorkspace =
    polledWorkspace?.Result && polledWorkspace.Result.workspace_id === workspaceId ? polledWorkspace.Result : undefined;
  const reportedDeadline = polledCurrentWorkspace
    ? polledCurrentWorkspace.auto_stop_after
    : currentWorkspace?.auto_stop_after;
  const deadline = reportedDeadline && reportedDeadline !== staleDeadline ? reportedDeadline : undefined;
  latestDeadlineRef.current = reportedDeadline;

  // workspaceContext only learns about a pause when the workgroup list is refetched, so it can keep
  // reporting Active long after the poll has seen the workspace stop. The poll wins when present.
  const currentStatus = polledCurrentWorkspace?.status ?? currentWorkspace?.status;
  const isTracking =
    enabled &&
    !!currentWorkspace &&
    autoStopEnabled &&
    hasPermission &&
    !!currentStatus &&
    isStatusActive(currentStatus);

  const sendKeepAlive = useCallback(() => {
    if (!currentWorkspace) {
      return;
    }
    lastKeepAliveAtRef.current = Date.now();
    keepAliveMutation.mutate(
      { group_id: currentWorkspace.workgroup_id, space_id: currentWorkspace.workspace_id },
      {
        onSuccess: (data) => {
          const serverTime = data?.Result?.server_time;
          if (serverTime) {
            skewMsRef.current = Date.parse(serverTime) - Date.now();
          }
        },
      }
    );
    // A failed ping is not worth interrupting the user over: the worst case is the workspace
    // pauses as it does today, and the resume prompt takes over.
  }, [currentWorkspace, keepAliveMutation]);

  // Countdown tick.
  useEffect(() => {
    if (!isTracking) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isTracking]);

  const msRemaining = useMemo(() => {
    if (!deadline) {
      return Number.POSITIVE_INFINITY;
    }
    const parsed = Date.parse(deadline);
    if (Number.isNaN(parsed)) {
      return Number.POSITIVE_INFINITY;
    }
    return Math.max(0, parsed - (now + skewMsRef.current));
  }, [deadline, now]);

  // The state machine: inside the warning window, either ping silently or prompt.
  useEffect(() => {
    if (!isTracking || !deadline || pausedWorkspace) {
      return;
    }
    if (msRemaining > AUTO_PAUSE_WARN_LEAD_MS) {
      return;
    }

    const optedIn = optedInWorkspaceIdRef.current === workspaceId;
    // Never extend behind an open warning: the user is in the middle of deciding, and the mouse
    // movement on the way to "Let it pause" would otherwise count as activity and extend it.
    if (optedIn && isActive() && !isWarningVisible) {
      const sinceLastPing = Date.now() - lastKeepAliveAtRef.current;
      if (!keepAliveMutation.isLoading && sinceLastPing >= KEEP_ALIVE_MIN_INTERVAL_MS) {
        sendKeepAlive();
      }
      return;
    }

    if (dismissedDeadlineRef.current === deadline) {
      return;
    }
    setWarningVisible(true);
  }, [
    isTracking,
    deadline,
    msRemaining,
    pausedWorkspace,
    workspaceId,
    isActive,
    isWarningVisible,
    sendKeepAlive,
    keepAliveMutation.isLoading,
  ]);

  const onKeepAlive = useCallback(() => {
    optedInWorkspaceIdRef.current = workspaceId ?? null;
    dismissedDeadlineRef.current = null;
    setWarningVisible(false);
    sendKeepAlive();
  }, [workspaceId, sendKeepAlive]);

  const onDismissWarning = useCallback(() => {
    lastKeepAliveAtRef.current = 0;
    dismissedDeadlineRef.current = deadline ?? null;
    // Declining also revokes a previous opt-in, otherwise the auto-ping would immediately undo it.
    optedInWorkspaceIdRef.current = null;
    setWarningVisible(false);
  }, [deadline]);

  const onDismissResume = useCallback(() => {
    // Only leave the workspace the prompt was about; the selection may have moved on already.
    if (pausedWorkspace && pausedWorkspace.workspace_id === currentWorkspace?.workspace_id) {
      leaveCurrentWorkspace();
    }
    setPausedWorkspace(null);
  }, [pausedWorkspace, currentWorkspace, leaveCurrentWorkspace]);

  const onResumed = useCallback(() => {
    resumingWorkspaceIdRef.current = pausedWorkspace?.workspace_id ?? null;
    setPausedWorkspace(null);
  }, [pausedWorkspace]);

  return {
    warningWorkspace: isWarningVisible && isTracking ? currentWorkspace ?? null : null,
    msRemaining: Number.isFinite(msRemaining) ? msRemaining : 0,
    pausedWorkspace,
    isExtending: keepAliveMutation.isLoading,
    onKeepAlive,
    onDismissWarning,
    onDismissResume,
    onResumed,
  };
}
